import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Mail, RefreshCw, AlertCircle, Eye, CheckCircle, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import adminService, { ContactSubmission, ContactSubmissionStatus, GetContactSubmissionsParams } from '../../services/admin/adminService';
import DataTable, { Column } from '../../components/common/DataTable';

const ContactSubmissionsManagement: React.FC = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statistics, setStatistics] = useState({
    pending: 0,
    unread: 0,
    total: 0
  });
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; submission: ContactSubmission | null }>({
    show: false,
    submission: null
  });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async (showToast = false) => {
    try {
      if (showToast) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const params: GetContactSubmissionsParams = {
        page: 1,
        limit: 100, // Fetch enough data for client-side pagination
      };

      const data = await adminService.getAllContactSubmissions(params);
      setSubmissions(data.submissions);
      setStatistics(data.statistics);
      
      if (showToast) {
        toast.success('Contact submissions refreshed!');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch contact submissions');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleDeleteSubmission = async () => {
    if (!deleteModal.submission) return;
    
    try {
      setDeleting(true);
      await adminService.deleteContactSubmission(deleteModal.submission.id);
      toast.success('Contact submission deleted successfully!');
      setDeleteModal({ show: false, submission: null });
      fetchSubmissions();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete contact submission');
    } finally {
      setDeleting(false);
    }
  };

  const handleViewSubmission = (submission: ContactSubmission) => {
    navigate(`/admin/contact-submissions/${submission.id}`);
  };

  if (loading && submissions.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Contact Submissions</h1>
          <p className="text-secondary-600 mt-1">Manage customer contact form submissions</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => fetchSubmissions(true)}
            disabled={refreshing}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-secondary-600">Total Submissions</div>
          <div className="text-2xl font-bold text-secondary-900 mt-1">{statistics.total}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-secondary-600">Pending</div>
          <div className="text-2xl font-bold text-yellow-600 mt-1">{statistics.pending}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-secondary-600">Unread</div>
          <div className="text-2xl font-bold text-blue-600 mt-1">{statistics.unread}</div>
        </div>
      </div>

      {/* Submissions Table */}
      <DataTable
        data={submissions}
        loading={loading}
        searchable={true}
        searchPlaceholder="Search by name, email, phone, or message..."
        searchFields={['name', 'email', 'phone', 'message', 'subject']}
        filterable={true}
        filterOptions={[
          { label: 'All', value: 'all', field: 'status' },
          { label: 'Pending', value: 'pending', field: 'status' },
          { label: 'In Progress', value: 'in_progress', field: 'status' },
          { label: 'Resolved', value: 'resolved', field: 'status' },
          { label: 'Closed', value: 'closed', field: 'status' },
          { label: 'Unread', value: 'unread', field: 'is_read' },
        ]}
        defaultPageSize={10}
        emptyMessage="No contact submissions found"
        emptyIcon={<Mail size={64} className="text-secondary-300 mx-auto mb-4" />}
        columns={[
          {
            header: 'Contact Info',
            accessor: (submission) => (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <Mail size={20} className="text-primary-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-secondary-900">
                    {submission.name}
                  </h3>
                  <p className="text-xs text-secondary-500">{submission.email}</p>
                  <p className="text-xs text-secondary-500">{submission.phone}</p>
                </div>
              </div>
            ),
            sortable: true,
          },
          {
            header: 'Subject',
            accessor: (submission) => (
              <div>
                <span className="text-sm font-medium text-secondary-900">{submission.subject}</span>
              </div>
            ),
            sortable: true,
          },
          {
            header: 'Message',
            accessor: (submission) => (
              <p className="text-sm text-secondary-600 max-w-md line-clamp-2">
                {submission.message}
              </p>
            ),
          },
          {
            header: 'Status',
            accessor: (submission) => (
              <span
                className={`inline-block px-3 py-1 text-xs rounded-full font-semibold ${
                  submission.status === 'resolved'
                    ? 'bg-green-100 text-green-800'
                    : submission.status === 'in_progress'
                    ? 'bg-blue-100 text-blue-800'
                    : submission.status === 'closed'
                    ? 'bg-gray-100 text-gray-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}
              >
                {submission.status === 'in_progress' ? 'In Progress' : submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
              </span>
            ),
            align: 'center',
            sortable: true,
          },
          {
            header: 'Read',
            accessor: (submission) => {
              const isRead = Boolean(submission.is_read);
              return (
                <div className="flex items-center justify-center">
                  {isRead ? (
                    <CheckCircle size={20} className="text-green-600" />
                  ) : (
                    <div className="relative">
                      <XCircle size={20} className="text-red-600" />
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                    </div>
                  )}
                </div>
              );
            },
            align: 'center',
            sortable: true,
          },
          {
            header: 'Date',
            accessor: (submission) => (
              <span className="text-sm text-secondary-600">
                {new Date(submission.created_at).toLocaleDateString()}
              </span>
            ),
            align: 'center',
            sortable: true,
          },
        ]}
        renderActions={(submission) => (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleViewSubmission(submission);
              }}
              className="bg-blue-50 hover:bg-blue-100 text-blue-600 p-2 rounded-lg transition-colors"
              title="View Submission"
            >
              <Eye size={18} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setDeleteModal({ show: true, submission });
              }}
              className="bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-lg transition-colors"
              title="Delete Submission"
            >
              <Trash2 size={18} />
            </button>
          </div>
        )}
        onRowClick={handleViewSubmission}
      />

      {/* Delete Confirmation Modal */}
      {deleteModal.show && deleteModal.submission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertCircle size={24} className="text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-secondary-900">Delete Submission</h3>
              </div>
              
              <p className="text-secondary-600 mb-6">
                Are you sure you want to delete the contact submission from{' '}
                <strong>{deleteModal.submission.name}</strong>? This action cannot be undone.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={handleDeleteSubmission}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  onClick={() => setDeleteModal({ show: false, submission: null })}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-gray-200 text-secondary-900 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactSubmissionsManagement;
